import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Combobox } from '@/components/ui/combobox';
import { Checkbox } from '@/components/ui/checkbox';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

interface BrandSupplierFormData {
  supplierId: string;
  isAuthorized: boolean;
  isPrimary: boolean;
  notes?: string;
}

interface BrandSupplierModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brandId: string;
  brandName: string;
  existingSupplierIds?: string[];
}

export function BrandSupplierModal({
  open,
  onOpenChange,
  brandId,
  brandName,
  existingSupplierIds = [],
}: BrandSupplierModalProps) {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');

  const {
    handleSubmit,
    setValue,
    watch,
    reset,
    register,
  } = useForm<BrandSupplierFormData>({
    defaultValues: {
      supplierId: '',
      isAuthorized: true,
      isPrimary: false,
      notes: '',
    },
  });

  const utils = trpc.useUtils();

  // Get list of suppliers for the dropdown
  const { data: suppliersData } = trpc.tradingSupplier.listForSelect.useQuery();

  // Filter out suppliers that are already assigned
  const availableSuppliers = suppliersData?.filter(
    (s) => !existingSupplierIds.includes(s.id)
  ) || [];

  const supplierOptions = availableSuppliers.map((s) => ({
    value: s.id,
    label: s.companyName,
  }));

  const createMutation = trpc.tradingBrandSupplier.create.useMutation({
    onSuccess: () => {
      utils.tradingBrandSupplier.getSuppliersByBrand.invalidate(brandId);
      showSuccessToast(tCommon('created'));
      onOpenChange(false);
      reset();
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const onSubmit = (data: BrandSupplierFormData) => {
    createMutation.mutate({
      brandId,
      supplierId: data.supplierId,
      isAuthorized: data.isAuthorized,
      isPrimary: data.isPrimary,
      notes: data.notes || undefined,
    });
  };

  const selectedSupplierId = watch('supplierId');
  const isSubmitting = createMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('brands.suppliers.addSupplier')}</DialogTitle>
          <DialogDescription>
            {t('brands.suppliers.subtitle')} - {brandName}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label>{t('brands.suppliers.supplier')}</Label>
            <Combobox
              options={supplierOptions}
              value={selectedSupplierId}
              onValueChange={(value) => setValue('supplierId', value)}
              placeholder={t('brands.suppliers.selectSupplier')}
              searchPlaceholder={tCommon('search')}
              emptyText={t('brands.suppliers.noSuppliers')}
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="isAuthorized"
              checked={watch('isAuthorized')}
              onCheckedChange={(checked) => setValue('isAuthorized', !!checked)}
            />
            <Label htmlFor="isAuthorized" className="cursor-pointer">
              {t('brands.suppliers.isAuthorized')}
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="isPrimary"
              checked={watch('isPrimary')}
              onCheckedChange={(checked) => setValue('isPrimary', !!checked)}
            />
            <Label htmlFor="isPrimary" className="cursor-pointer">
              {t('brands.suppliers.isPrimary')}
            </Label>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">{t('brands.suppliers.notes')}</Label>
            <Textarea
              id="notes"
              {...register('notes')}
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {tCommon('cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting || !selectedSupplierId}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {tCommon('create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
